import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const itemCount = this.refs.cartBubbleCount.dataset.itemCount;

    return !!itemCount ? parseInt(itemCount, 10) || 0 : 0;
  }

  set currentCartCount(value) {
    const sessionData = {
      value: String(value),
      timestamp: Date.now(),
    };

    sessionStorage.setItem('cart-count', JSON.stringify(sessionData));
    this.refs.cartBubbleCount.dataset.itemCount = String(value);
    this.#updateDisplay(value);
  }

  /**
   * Updates the display text based on the actual cart count
   */
  #updateDisplay(count) {
    const MAX_COUNT = 99;

    const displayValue = count <= 0 ? '0' : count <= MAX_COUNT ? String(count) : `${MAX_COUNT}+`;

    this.refs.cartBubbleCount.textContent = displayValue;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromAddToCart =
      event.detail.data?.source === 'product-form-component' ||
      event.detail.data?.source === 'constructor-add-to-cart' ||
      event.detail.data?.source === 'content-card-add-to-cart' ||
      event.detail.data?.source === 'buy-again';

    this.renderCartBubble(itemCount, comingFromAddToCart);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromAddToCart - Whether the cart update is coming from an add to cart action.
   * @param {boolean} animate - Whether to animate the cart bubble.
   */
  renderCartBubble = async (itemCount, comingFromAddToCart, animate = true) => {
    // If the cart update is coming from an add to cart action, we add to the current cart count, otherwise we set the new cart count
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);
    this.currentCartCount = comingFromAddToCart ? this.currentCartCount + itemCount : itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (!animate) return;

    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageData = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.dataset.itemCount || '0';

    if (!sessionStorageData) return;

    try {
      const sessionStorageDataObject = JSON.parse(sessionStorageData);
      const sessionStorageDataValue = sessionStorageDataObject ? sessionStorageDataObject.value : null;
      const sessionStorageDataTimestamp = sessionStorageDataObject ? sessionStorageDataObject.timestamp : null;

      if (sessionStorageDataValue === visibleCount || sessionStorageDataValue === null) return;

      if (Date.now() - sessionStorageDataTimestamp < 10000) {
        const count = parseInt(sessionStorageDataValue, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
